import { ProfileBanner, ContactMe, TimelineItem, Project, Skill } from '../types';

// ============================================================
// PROFILE BANNER - Shown on the main profile page
// ============================================================
export const profileBannerData: ProfileBanner = {
  backgroundImage: { url: '' },
  headline: "Dev Ambani | Senior ML Engineer",
  resumeLink: { url: 'https://drive.google.com/file/d/1YFKQTH17hmZzwY_Y-uGor4UKkBLOqWkM/view?usp=sharing' },
  linkedinLink: 'https://www.linkedin.com/in/devambani/',
  profileSummary:
    "Senior ML Engineer with a graduate degree in AI and over 4 years of professional experience building intelligent systems that solve tangible problems. Passionate about bridging the gap between research and real world impact, from scalable ML pipelines to production grade models that drive decisions.", // TODO: Update with your full summary
};

// ============================================================
// CONTACT ME
// ============================================================
export const contactMeData: ContactMe = {
  profilePicture: { url: '' },
  name: 'Dev Ambani',
  title: 'Senior ML Engineer',
  summary: 'Passionate about ML, AI, and building data-driven products.',
  companyUniversity: 'xMentium',
  linkedinLink: 'https://www.linkedin.com/in/devambani/',
  email: 'dev.ambani01@gmail.com',
  phoneNumber: '',
  newsletterLink: 'https://whats-up-dev.beehiiv.com/?modal=signup',
  mediumLink: 'https://medium.com/@dev.ambani',
  twitterLink: 'https://x.com/thenakliambani',
  instagramLink: 'https://www.instagram.com/notthatambani/',
};

// ============================================================
// WORK EXPERIENCE & EDUCATION TIMELINE
// ============================================================
export const timelineData: TimelineItem[] = [
  {
    timelineType: 'work',
    name: 'xMentium',
    title: 'Machine Learning Engineer',
    techStack: '',
    summaryPoints: [
      'Deployed fine-tuned LLMs for automated legal contract generation, reducing drafting time by 60%. Built a vector search and RAG system on AWS improving contextual accuracy by 25%.',
    ],
    dateRange: 'May 2024 - Present',
  },
  {
    timelineType: 'work',
    name: 'Grainger',
    title: 'Machine Learning Engineer Intern',
    techStack: '',
    summaryPoints: [
      'Developed RAG systems to power intelligent product search and recommendations across over a million SKUs, improving retrieval relevance and customer experience.',
    ],
    dateRange: 'Jan 2024 - May 2024',
  },
  {
    timelineType: 'education',
    name: 'Northwestern University',
    title: 'M.S. in Artificial Intelligence (Honors), Minor in Engineering Management (Kellogg)',
    techStack: '',
    summaryPoints: [],
    dateRange: 'Sep 2023 - Dec 2024',
  },
  {
    timelineType: 'work',
    name: 'Sapours Technologies',
    title: 'AI Product Development Lead',
    techStack: '',
    summaryPoints: [
      'Built deep learning and reinforcement learning systems that raised forecasting accuracy to 93% and optimized revenue by 47%. Engineered recommendation systems boosting retention by 25%.',
    ],
    dateRange: 'Dec 2020 - Dec 2023',
  },
  {
    timelineType: 'work',
    name: 'Capgemini',
    title: 'Data Scientist Intern',
    techStack: '',
    summaryPoints: [
      'Built anomaly detection models achieving 95% accuracy for financial fraud detection. Automated data preprocessing, reducing manual cleaning by 42%.',
    ],
    dateRange: 'Aug 2020 - Oct 2020',
  },
  {
    timelineType: 'education',
    name: 'Mumbai University',
    title: 'B.E. in Electronics & Communication Engineering, Minor in Computer Science (Magna Cum Laude)',
    techStack: '',
    summaryPoints: [],
    dateRange: 'Aug 2019 - Jun 2023',
  },
];

// ============================================================
// PROJECTS
// ============================================================
export const projectsData: Project[] = [
  {
    title: 'Open Communities: Fair Housing Detector',
    description: 'An AI-powered tool that scans rental listings across major housing platforms for potentially discriminatory language. The system flags suspicious listings for human reviewers who verify, tag, and confirm findings, combining automated pattern detection with human judgment to protect fair housing rights.',
    techUsed: 'Python, NLP, Web Scraping, AI',
    image: { url: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&h=400&fit=crop' },
    link: 'https://www.ocfairhousingtool.com/',
    tags: ['NLP', 'Social Impact', 'AI Ethics'],
  },
  {
    title: 'MoonSight: AI Market Research Platform',
    description: 'A platform that lets businesses survey real college students and get AI-powered insights in under 24 hours. Type a question, reach 100 verified respondents, and receive instant visual analytics with actionable recommendations. Built for the startups and local businesses that big survey firms overlook.',
    techUsed: 'React, Node.js, AI, Stripe, Analytics',
    image: { url: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop' },
    link: 'https://moonsights.ai/',
    tags: ['AI', 'SaaS', 'Market Research'],
  },
  {
    title: 'DataBites: Museum of Science Exhibit',
    description: 'An interactive museum exhibit at the Griffin Museum of Science and Industry in Chicago that teaches children how supervised machine learning works. Visitors collaboratively create labeled training data using tangible pieces, then watch a live ML model learn to classify their creations in real time. Featured at the 16th Conference on Creativity & Cognition.',
    techUsed: 'Python, Machine Learning, Computer Vision',
    image: { url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=400&fit=crop' },
    link: 'https://scholar.google.com/citations?view_op=view_citation&hl=en&user=izywShwAAAAJ&citation_for_view=izywShwAAAAJ:u5HHmVD_uO8C',
    tags: ['Research', 'Published', 'Museum Exhibit'],
  },
  {
    title: 'Subway Surfers: Computer Vision Controller',
    description: 'Why use a keyboard when you have a body? This project turns your webcam into a game controller using MediaPipe pose detection and computer vision. Jump, crouch, dodge left and right, all by physically moving in front of your camera. A fun intersection of computer vision and gaming that makes you break a sweat.',
    techUsed: 'Python, MediaPipe, OpenCV, PyAutoGUI',
    image: { url: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=600&h=400&fit=crop' },
    link: 'https://github.com/DevAmbani/SubwaySurfersCV',
    tags: ['Computer Vision', 'Pose Detection', 'Gaming'],
  },
  {
    title: 'SameSame: Sentiment Analysis Chatbot',
    description: 'A chatbot that doesn\'t just respond, it listens. Uses BERT and logistic regression to classify user emotional states in real time, then adapts its conversational strategy accordingly. Designed to detect signs of distress and escalate sensitively, making AI interactions feel genuinely human.',
    techUsed: 'Python, BERT, OpenAI, Chainlit',
    image: { url: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop' },
    link: 'https://github.com/DevAmbani/MentalHealthChatbot',
    tags: ['NLP', 'Deep Learning', 'Mental Health'],
  },
  {
    title: 'Moo: Answer, Earn, Risk',
    description: 'A mobile app that turns survey participation into a game. College students answer market research questions for local businesses, earn rewards, and risk their earnings on bonus challenges. A gamified feedback loop that keeps engagement high and data quality even higher.',
    techUsed: 'Swift, iOS, Firebase, Gamification',
    image: { url: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&h=400&fit=crop' },
    link: 'https://apps.apple.com/us/app/moo-answer-earn-risk/id6758931715',
    tags: ['Mobile', 'iOS', 'Gamification'],
  },
  {
    title: 'SmartRoads: Urban Mobility Control',
    description: 'An AI-driven traffic system built during a research stint at DJ Sanghvi\'s Signal & Systems Lab. A novel computer vision algorithm running on Raspberry Pi dynamically processes traffic flow, reducing congestion by 15% and improving travel times by 10%. Selected for the "Smart India" initiative and published with IETE.',
    techUsed: 'Python, Computer Vision, Raspberry Pi, IoT',
    image: { url: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=600&h=400&fit=crop' },
    link: 'https://scholar.google.com/citations?view_op=view_citation&hl=en&user=izywShwAAAAJ&citation_for_view=izywShwAAAAJ:u-x6o8ySG0sC',
    tags: ['Research', 'Published', 'IoT', 'Smart City'],
  },
  {
    title: 'AirAI: Smart Air Quality Monitor',
    description: 'An IoT-powered environmental monitoring system that combines GSM connectivity with machine learning to track air quality, detect gas leaks, and trigger fire alarms in real time. An SVM model adapts to evolving conditions, boosting detection accuracy by 30%. Deployed as a working prototype and published with IETE.',
    techUsed: 'Python, IoT, SVM, GSM, Data Analytics',
    image: { url: 'https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?w=600&h=400&fit=crop' },
    link: 'https://scholar.google.com/citations?view_op=view_citation&hl=en&user=izywShwAAAAJ&citation_for_view=izywShwAAAAJ:d1gkVwhDpl0C',
    tags: ['Research', 'Published', 'IoT', 'Environmental'],
  },
  {
    title: 'CoachEYE: Soccer Analytics Assistant',
    description: 'A RAG-powered coaching assistant built for Northwestern\'s soccer team. CoachEYE ingests match and player data into a Chroma vector database, letting coaches ask natural language questions about tactics, player development, and performance trends. Think of it as having a data analyst on the sideline who never sleeps.',
    techUsed: 'Python, Flask, Chroma, OpenAI, RAG',
    image: { url: 'https://images.unsplash.com/photo-1551958219-acbc608c6377?w=600&h=400&fit=crop' },
    link: 'https://github.com/DevAmbani/CoachEYE',
    tags: ['RAG', 'NLP', 'Sports Analytics'],
  },
];

// ============================================================
// SKILLS
// ============================================================
export const skillsData: Skill[] = [
  // Machine Learning & AI
  { name: 'PyTorch', category: 'Machine Learning & AI', description: 'Deep learning & model training', icon: 'SiPytorch' },
  { name: 'TensorFlow', category: 'Machine Learning & AI', description: 'Production ML pipelines', icon: 'SiTensorflow' },
  { name: 'Scikit-learn', category: 'Machine Learning & AI', description: 'Classical ML algorithms', icon: 'SiScikitlearn' },
  { name: 'Hugging Face', category: 'Machine Learning & AI', description: 'LLMs & transformers', icon: 'SiHuggingface' },
  { name: 'OpenCV', category: 'Machine Learning & AI', description: 'Computer vision', icon: 'SiOpencv' },
  { name: 'LangChain', category: 'Machine Learning & AI', description: 'LLM orchestration & RAG', icon: 'SiLangchain' },
  { name: 'spaCy', category: 'Machine Learning & AI', description: 'NLP pipelines', icon: 'SiSpacy' },
  { name: 'Keras', category: 'Machine Learning & AI', description: 'High-level neural networks', icon: 'SiKeras' },
  { name: 'ONNX', category: 'Machine Learning & AI', description: 'Model interoperability', icon: 'SiOnnx' },
  { name: 'SciPy', category: 'Machine Learning & AI', description: 'Scientific computing', icon: 'SiScipy' },
  { name: 'Plotly', category: 'Machine Learning & AI', description: 'Interactive visualization', icon: 'SiPlotly' },

  // Databases
  { name: 'PostgreSQL', category: 'Databases', description: 'Relational database', icon: 'SiPostgresql' },
  { name: 'MySQL', category: 'Databases', description: 'Relational database', icon: 'SiMysql' },
  { name: 'MongoDB', category: 'Databases', description: 'Document store', icon: 'SiMongodb' },
  { name: 'Redis', category: 'Databases', description: 'In-memory cache & store', icon: 'SiRedis' },
  { name: 'Pinecone', category: 'Databases', description: 'Vector database for embeddings', icon: 'SiPinecone' },
  { name: 'Elasticsearch', category: 'Databases', description: 'Search & analytics engine', icon: 'SiElasticsearch' },
  { name: 'DynamoDB', category: 'Databases', description: 'Serverless NoSQL', icon: 'SiAmazondynamodb' },
  { name: 'Neo4j', category: 'Databases', description: 'Graph database', icon: 'SiNeo4j' },
  { name: 'Snowflake', category: 'Databases', description: 'Cloud data warehouse', icon: 'SiSnowflake' },
  { name: 'Cassandra', category: 'Databases', description: 'Distributed NoSQL', icon: 'SiApachecassandra' },
  { name: 'Firebase', category: 'Databases', description: 'Real-time database', icon: 'SiFirebase' },
  { name: 'Supabase', category: 'Databases', description: 'Open-source Firebase alt', icon: 'SiSupabase' },

  // Cloud & Infrastructure
  { name: 'AWS', category: 'Cloud & Infrastructure', description: 'SageMaker, Lambda, S3, EC2', icon: 'FaAws' },
  { name: 'GCP', category: 'Cloud & Infrastructure', description: 'Vertex AI, BigQuery, GCE', icon: 'SiGooglecloud' },
  { name: 'Docker', category: 'Cloud & Infrastructure', description: 'Containerization', icon: 'FaDocker' },
  { name: 'Kubernetes', category: 'Cloud & Infrastructure', description: 'Container orchestration', icon: 'SiKubernetes' },
  { name: 'Terraform', category: 'Cloud & Infrastructure', description: 'Infrastructure as code', icon: 'SiTerraform' },
  { name: 'GitHub Actions', category: 'Cloud & Infrastructure', description: 'CI/CD pipelines', icon: 'SiGithubactions' },

  // Programming Languages
  { name: 'Python', category: 'Programming Languages', description: 'Primary language', icon: 'FaPython' },
  { name: 'SQL', category: 'Programming Languages', description: 'Data querying & manipulation', icon: 'SiSqlite' },
  { name: 'TypeScript', category: 'Programming Languages', description: 'Typed web development', icon: 'SiTypescript' },
  { name: 'Java', category: 'Programming Languages', description: 'Backend services', icon: 'FaJava' },
  { name: 'C++', category: 'Programming Languages', description: 'Performance-critical systems', icon: 'SiCplusplus' },
  { name: 'Bash', category: 'Programming Languages', description: 'Scripting & automation', icon: 'SiGnubash' },

  // Data Engineering
  { name: 'Apache Spark', category: 'Data Engineering', description: 'Distributed data processing', icon: 'SiApachespark' },
  { name: 'Apache Kafka', category: 'Data Engineering', description: 'Event streaming', icon: 'SiApachekafka' },
  { name: 'Apache Airflow', category: 'Data Engineering', description: 'Workflow orchestration', icon: 'SiApacheairflow' },
  { name: 'Pandas', category: 'Data Engineering', description: 'Data manipulation', icon: 'SiPandas' },
  { name: 'NumPy', category: 'Data Engineering', description: 'Numerical computing', icon: 'SiNumpy' },
  { name: 'dbt', category: 'Data Engineering', description: 'Data transformations', icon: 'SiDbt' },

  // MLOps & Tools
  { name: 'MLflow', category: 'MLOps & Tools', description: 'Experiment tracking & registry', icon: 'SiMlflow' },
  { name: 'Weights & Biases', category: 'MLOps & Tools', description: 'ML experiment tracking', icon: 'SiWeightsandbiases' },
  { name: 'Git', category: 'MLOps & Tools', description: 'Version control', icon: 'SiGit' },
  { name: 'Linux', category: 'MLOps & Tools', description: 'System administration', icon: 'FaLinux' },
  { name: 'Jupyter', category: 'MLOps & Tools', description: 'Interactive notebooks', icon: 'SiJupyter' },
  { name: 'FastAPI', category: 'MLOps & Tools', description: 'Model serving APIs', icon: 'SiFastapi' },
];

// ============================================================
// PUBLICATIONS
// ============================================================
export interface Publication {
  title: string;
  authors: string;
  venue: string;
  year: string;
  link: string;
  abstract: string;
  citations?: number;
  image?: string;
}

export const publicationsData: Publication[] = [
  {
    title: 'DataBites: An Embodied and Co-Creative Museum Exhibit to Foster Children\'s Understanding of Supervised Machine Learning',
    authors: 'Hasti Darabipourshiraz, Dev Ambani, Duri Long',
    venue: 'Proceedings of the 16th Conference on Creativity & Cognition',
    year: '2024',
    link: 'https://scholar.google.com/citations?view_op=view_citation&hl=en&user=izywShwAAAAJ&citation_for_view=izywShwAAAAJ:u5HHmVD_uO8C',
    abstract: 'An interactive museum exhibit at the Griffin Museum of Science and Industry that teaches children supervised machine learning through embodied interaction, creativity, and collaboration. Visitors create labeled examples to train a live image classification algorithm and evaluate its performance.',
    citations: 13,
    image: 'museum',
  },
  {
    title: 'AI Image-Based Urban Mobility Recommendation System',
    authors: 'M Pimpale, D Ambani, D Gonzalves, A Gandhi',
    venue: 'Spark, Volume 7 (IETE)',
    year: '2022',
    link: 'https://scholar.google.com/citations?view_op=view_citation&hl=en&user=izywShwAAAAJ&citation_for_view=izywShwAAAAJ:u-x6o8ySG0sC',
    abstract: 'A computer vision system that processes real-time traffic imagery to generate intelligent urban mobility recommendations, improving congestion management and travel efficiency in smart city environments.',
    citations: 7,
    image: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=600&h=400&fit=crop',
  },
  {
    title: 'IOT-Enabled Adaptive Air Quality Monitoring System',
    authors: 'S Bhattacharjee, D Ambani, D Jain',
    venue: 'Spark, Volume 6 (IETE)',
    year: '2021',
    link: 'https://scholar.google.com/citations?view_op=view_citation&hl=en&user=izywShwAAAAJ&citation_for_view=izywShwAAAAJ:d1gkVwhDpl0C',
    abstract: 'An IoT-powered environmental monitoring system with GSM connectivity and adaptive machine learning that tracks air quality, detects hazards, and provides real-time alerts for safer indoor environments.',
    citations: 4,
    image: 'https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?w=600&h=400&fit=crop',
  },
];

// ============================================================
// BLOGS
// ============================================================
export interface BlogPost {
  title: string;
  platform: string;
  link: string;
  description: string;
}

export const blogsData: BlogPost[] = [
  {
    title: 'AI Won\'t Replace You — But It Might Make You Replaceable',
    platform: 'Medium',
    link: 'https://medium.com/@dev.ambani/ai-wont-replace-you-but-it-might-make-you-replaceable-45a10ebecf44',
    description: 'AI isn\'t going to replace most jobs. It\'s going to shrink how many people companies need to do them. The real threat isn\'t replacement — it\'s compression. A look at why staying valuable means combining technical ability with judgment, context, and distinctly human strengths.',
  },
  {
    title: 'Maybe Don\'t Use AI — At Least Not Always',
    platform: 'Medium',
    link: 'https://medium.com/@dev.ambani/maybe-dont-use-ai-at-least-not-always-961bb4ff90f9',
    description: 'Just because we can automate something doesn\'t mean we should. A reflection on when AI subtracts more than it adds, from a talk at the Shapeshift Summit. The piece explores how intentional constraints can protect the creativity, care, and trust that automation sometimes erodes.',
  },
  {
    title: 'The Most Underrated AI Skill? Knowing When Not to Use It.',
    platform: 'Medium',
    link: 'https://medium.com/@dev.ambani/the-most-underrated-ai-skill-knowing-when-not-to-use-it-45233333e81f',
    description: 'The most useful AI skill right now isn\'t prompt engineering. It\'s knowing when not to use it. On resisting the urge to automate everything. A practical case for choosing tools that serve the problem instead of forcing every problem through the latest tool.',
  },
  {
    title: 'When AI Does the Work, What\'s Left Is Soul',
    platform: 'Medium',
    link: 'https://medium.com/@dev.ambani/when-ai-does-the-work-whats-left-is-soul-9997caaa7a62',
    description: 'What if AI doesn\'t make us less human — but more? By offloading the mechanical, we return to empathy, originality, and the stuff that doesn\'t scale but matters infinitely. An argument for using the time AI saves to invest more deliberately in the work that requires presence and care.',
  },
];

// ============================================================
// RECOMMENDATIONS
// ============================================================
export interface Recommendation {
  name: string;
  title: string;
  date: string;
  profilePic?: string;
  body: string[];
  link?: string;
}

export const recommendationsData: Recommendation[] = [
  {
    name: 'Dr. Mohammed A. Alam',
    title: 'Director of MSAI at Northwestern University',
    date: '',
    profilePic: 'alam',
    body: [
      'Dev\'s is one of the top three names that come to mind when someone asks me to recommend someone to hire from our talent pool of graduate students here at Northwestern. During his time here in the MSAI program, I found him to be consistently impressive in ways that go beyond technical expertise.',
      'He is an incisive thinker and has a ton of enthusiasm for all things AI. What separates him from most others, though, is what else is needed to be truly successful in the AI industry. That includes his professionalism, his ability to respect others and gain respect, the rare quality of being able to communicate his work well to others, and his all-roundedness as an AI professional and as a person.',
      'I have absolutely no doubt that Dev will go far and make a noteworthy impact in the world of AI.',
    ],
  },
  {
    name: 'Dr. Alex Castrounis',
    title: 'Kellogg Professor, Founder of Why of AI',
    date: '',
    profilePic: 'alex',
    body: [
      'Dev served as a Graduate Assistant (GA) for my course at Northwestern\'s Kellogg/McCormick MBAi program, Applied AI for Business. He consistently went above and beyond in supporting class planning, grading assignments, and helping to keep course content current by researching the latest AI trends and developments.',
      'Dev also made a big impact on students by holding office hours, staying consistently available and responsive, and offering clear, helpful guidance on their projects and assignments. He is proactive, reliable, and deeply committed to helping others succeed. I\'d highly recommend Dev to any team.',
    ],
  },
  {
    name: 'Dr. Duri Long',
    title: 'Director of Creative AI Interface Research, Northwestern University',
    date: '',
    profilePic: 'duri',
    body: [
      'Dev is a talented AI engineer who can innovate to solve complex problems and communicate clearly with colleagues from interdisciplinary backgrounds. Dev worked for me as a Research Assistant for a year and a half in the Creative Interfaces Research + Design Studio at Northwestern University.',
      'He was our lead machine learning developer for a museum exhibit we were designing as part of a multi-institutional government-funded project to foster broader public AI literacy. Dev consistently came up with creative solutions to challenges like how to allow kids to observe their impact on the AI\'s output, even when they were only able to create a very small dataset.',
      'Dev is a pleasure to work with\u2014hard-working, communicative, quick to meet a deadline, and above all enthusiastic about his work. I was most impressed by his ability to clearly communicate complex machine learning concepts to a team of interdisciplinary researchers. He would be a valuable asset to any team.',
    ],
  },
  {
    name: 'Samir Gandhi',
    title: 'Co-Founder & CEO, Sapours Technologies',
    date: '',
    profilePic: 'samir',
    body: [
      'Dev joined Sapours as an eager talent and quickly proved himself to be one of the most driven and resourceful engineers on our team. Over three years, he grew from building initial prototypes to leading our entire AI product development vertical.',
      'What stood out most was his ability to take ownership of complex, ambiguous problems and deliver production-ready solutions. Whether it was designing deep learning models for customer behavior prediction or architecting recommendation systems, Dev brought both technical depth and a product mindset that is rare to find.',
      'He is a natural leader who earns the trust of his teammates through hard work, clear communication, and genuine care for the people around him. I have no hesitation in recommending Dev for any role that demands both technical excellence and strong leadership.',
    ],
    link: 'https://drive.google.com/drive/folders/1cUIAPvK2Sw9EHNrA4mJ5B6-HqTe7EE6F?dmr=1&ec=wgc-drive-hero-goto',
  },
];
